// apps/roleforge-app/src/lib/cap-error.ts
// Plan 3b-ii — Phase 3 (§8.6): Parse cap / activation-gate errors from stream events.

export interface CapError {
  kind?: string;
  /**
   * The raw server code. `kind` is the copy-map bucket (it collapses into
   * `cap_kind` when present), so precise branching — e.g. "expired window" vs
   * "no entitlement at all" vs "cap reached" — must use this.
   */
  code?: string;
  message: string;
  /**
   * Issue #1324 — the authoritative ORG-WIDE figures when the server sent them
   * (`used`, `cap`, `scope`). Present on the invitation 402s; absent on stream
   * frames that carry only a code.
   */
  used?: number;
  cap?: number;
  scope?: string;
}

const CAP_CODES = new Set([
  'cap_exceeded',
  'pre_activation_cap_exceeded',
  'pre_activation_expired',
  // Issue #1324: an org with no pre-activation sponsorship has no free invite
  // slots to spend. An entitlement answer, so it belongs in the cap family.
  'invite_not_entitled',
  'expired',
]);

/**
 * Returns a CapError if `err` is a cap / entitlement error; null otherwise.
 *
 * Accepts the raw event object from streamConverse (type: 'error') or any
 * error-like object.
 */
export function parseCapError(err: unknown): CapError | null {
  if (!err || typeof err !== 'object') return null;

  const own = err as Record<string, unknown>;
  // `ApiError.details` is the parsed HTTP error body (issue #1324); a stream
  // `{type:'error'}` frame is already flat. Own fields win so `message` stays
  // the Error message.
  //
  // NOTE: read through property access, never object spread — `Error.message`
  // is an own but NON-enumerable property, so spreading an ApiError silently
  // loses the message.
  const detailsBag =
    own.details && typeof own.details === 'object'
      ? (own.details as Record<string, unknown>)
      : {};
  const field = (key: string): unknown =>
    own[key] !== undefined ? own[key] : detailsBag[key];

  const code = typeof field('code') === 'string' ? (field('code') as string) : '';
  if (!CAP_CODES.has(code)) return null;

  // Normalise cap_kind or code into a kind bucket
  const capKind = field('cap_kind');
  const message = field('message');
  const used = field('used');
  const cap = field('cap');
  const scope = field('scope');

  return {
    kind: typeof capKind === 'string' ? capKind : code,
    code,
    message: typeof message === 'string' ? message : 'Limit reached.',
    used: typeof used === 'number' ? used : undefined,
    cap: typeof cap === 'number' ? cap : undefined,
    scope: typeof scope === 'string' ? scope : undefined,
  };
}

// ---------------------------------------------------------------------------
// Copy map
// ---------------------------------------------------------------------------

interface CapCopy {
  title: string;
  body: string;
  cta?: { label: string; href: string };
}

const COPY: Record<string, CapCopy> = {
  credits: {
    title: "You've used all your AI credits",
    body: 'Upgrade your plan to continue using AI-powered features.',
    cta: { label: 'Upgrade plan', href: '/billing' },
  },
  interviews: {
    title: "Interview limit reached",
    body: 'You have reached the maximum number of discovery interviews on your current plan. Upgrade to continue.',
    cta: { label: 'View plans', href: '/billing' },
  },
  per_call: {
    title: "Message limit reached",
    body: 'This conversation has hit its per-call message limit. Start a new audit or upgrade for larger sessions.',
  },
  expired: {
    title: "Your trial has expired",
    body: 'Your free trial has ended. Upgrade to keep using RoleForge.',
    cta: { label: 'Upgrade now', href: '/billing' },
  },
  pre_activation_cap_exceeded: {
    title: "Free usage limit reached",
    body: 'You have reached the limit of free RoleForge usage. Activate a plan to continue.',
    cta: { label: 'Activate a plan', href: '/billing' },
  },
  pre_activation_expired: {
    title: "Free trial expired",
    body: 'Your free access period has ended. Activate a plan to continue using RoleForge.',
    cta: { label: 'Activate a plan', href: '/billing' },
  },
  // Issue #1324: reachable for an org that never had free invite slots at all —
  // do not tell them they used something up.
  invite_not_entitled: {
    title: 'Stakeholder invites need a plan',
    body: 'Stakeholder invites are included with a VibeControl trial or plan. Activate one to bring your team into the audit.',
    cta: { label: 'Activate a plan', href: '/billing' },
  },
};

const FALLBACK: CapCopy = {
  title: "Usage limit reached",
  body: 'You have reached a usage limit. Please upgrade your plan or contact support.',
  cta: { label: 'View plans', href: '/billing' },
};

export function getCapCopy(cap: CapError): CapCopy {
  if (cap.kind && COPY[cap.kind]) return COPY[cap.kind];
  return FALLBACK;
}
